import { type UserGroup } from '#app/__generated__/api.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';

/**
 * Given a UserGroup or UserGroup array, returns a boolean indicating whether the current
 * user is a member of the specified group(s).
 *
 * Optionally you may pass an array of UserGroups to check against specific user's groups
 * (i.e. not the current user).
 * @example
 * // Checks against the current user's groups
 * const hasCompletedProfile = useHasUserGroups('CONTRACTOR_PROFILE_COMPLETE');
 *
 * // Checks against a specific user accounts groups
 * const hasAdminAccess = useHasUserGroups('ADMIN', userAccount.userGroups);
 */
export const useHasUserGroups = (
  hasGroups: UserGroup | UserGroup[],
  userGroups?: readonly UserGroup[],
): boolean => {
  const currentUser = useCurrentUser();

  const groupsToCheckAgainst: readonly UserGroup[] =
    userGroups ?? currentUser?.userGroups ?? [];

  if (Array.isArray(hasGroups)) {
    return hasGroups.every((group) => groupsToCheckAgainst.includes(group));
  }

  return groupsToCheckAgainst.includes(hasGroups);
};
